import { useRef, useState } from "react";

import {
  faCopy,
  faExclamationTriangle,
  faEye,
  faFile,
  faInputPipe,
} from "@fortawesome/pro-regular-svg-icons";
import { AxiosError } from "axios";
import { AnimatePresence, m } from "framer-motion";
import { capitalize, times } from "lodash-es";
import { createPortal } from "react-dom";
import { twJoin } from "tailwind-merge";

import { Provider } from "src/api/connectApi/types";
import { UseUploadFileReturn, useFile } from "src/api/filesApi/queries";
import { GenericObject } from "src/api/flowTypes";
import {
  ReviewCaseExternalReport,
  ReviewCaseHighlightsElement,
  ReviewCaseHighlightsFile,
} from "src/api/types";
import { FilePill } from "src/base-components/FilePill";
import { MarkdownRenderer } from "src/base-components/MarkdownRenderer/MarkdownRenderer";
import { MultilineDataValue } from "src/base-components/MultilineDataValue";
import { Pill } from "src/base-components/Pill";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { Tabs } from "src/base-components/Tabs";
import {
  FileAttributes,
  GetFileEnvFilesFileIdGetEnvEnum,
} from "src/clients/files";
import { ProviderIcon } from "src/connections/config/Icon";
import { getProviderName } from "src/connections/ProviderResourceProperties";
import { JSONValue } from "src/datasets/DatasetTable/types";
import { ObjectDetailPane } from "src/dataTable/ObjectDetailPane";
import { ObjectDetailPopover } from "src/dataTable/ObjectDetailPopover";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon, IconButton } from "src/design-system/Icon";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { FileDownloadButton, FileViewer } from "src/files/FileViewer";
import { ExcludesFalse } from "src/flow/types";
import { SkeletonLine } from "src/manualReview/reviewCaseCommon/SkeletonLine";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { copyTextToClipboard } from "src/utils/clipboard";
import { shortDateFromNow } from "src/utils/datetime";
import {
  FILE_TYPES,
  getFilePillTitle,
  humanReadableSize,
  isFileId,
} from "src/utils/files";
import { formatNumber } from "src/utils/numbers";
import {
  speakPythonPrimitive,
  stringifiedObjectSpeakPython,
} from "src/utils/speakPython";
import { isEmail, isProbablyMarkdown, isValidURL } from "src/utils/stringUtils";
import { assertUnreachable } from "src/utils/typeUtils";

const CARD_VERTICAL_MARGIN = "32px";
const TABS_HEIGHT = "47px";

export const Card: React.FC<{
  env?: GetFileEnvFilesFileIdGetEnvEnum;
  isLoading?: boolean;
  highlights: ReviewCaseHighlightsElement[];
  inspectData?: GenericObject;
  externalData?: ReviewCaseExternalReport[];
  isPreview?: boolean;
  fileUpload: UseUploadFileReturn;
}> = ({
  env,
  isLoading,
  highlights,
  inspectData,
  externalData,
  isPreview,
  fileUpload,
}) => {
  const humanifyExternalReportName = (
    resource: string,
    provider?: Provider,
  ) => {
    let providerName = null;
    if (provider) {
      try {
        providerName = getProviderName(provider);
      } catch {
        providerName = provider;
      }
    }
    const providerPart = providerName ? providerName + " " : "";

    const resourcePart = resource.split("_").map(capitalize).join(" ");

    return providerPart + resourcePart;
  };

  const existingFiles = highlights.filter(
    (highlight) => highlight.type === "file",
  );

  const uploadedFiles = fileUpload.getAllFiles();

  const files: Array<ReviewCaseHighlightsFile | File> = [
    ...existingFiles,
    ...uploadedFiles.values(),
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        className="mx-auto min-h-[400px] w-[662px] overflow-hidden rounded-xl border border-gray-200 bg-white"
        style={{
          marginTop: CARD_VERTICAL_MARGIN,
          marginBottom: CARD_VERTICAL_MARGIN,
        }}
      >
        {!isLoading ? (
          <Tabs
            containerClassName="flex flex-col max-h-full"
            defaultActiveKey="highlights"
            panelsClassName="overflow-auto min-h-0 decideScrollbar"
            tabClassName="px-1 py-3 mr-2 flex-shrink-0"
            tabListClassName={`px-4 pb-0.5 border-b border-gray-200 flex overflow-auto flex-shrink-0 h-[${TABS_HEIGHT}]`}
            tabs={[
              {
                key: "highlights",
                label: "Highlights",
                content: (
                  <HighlightsDisplay
                    env={env}
                    highlights={highlights}
                    isPreview={isPreview}
                  />
                ),
              },

              !!inspectData && {
                key: "inspect_data",
                label: "Inspect Data",
                content: <InspectDataDisplay data={inspectData} env={env} />,
              },
              {
                key: "files",
                label: (
                  <span className="flex items-center gap-1">
                    Files
                    {!isPreview && files.length > 0 && (
                      <span className="flex items-center rounded bg-indigo-50 pr-1">
                        <>
                          <Icon variant="active" icon={faFile} size="xs" />
                          <span className="font-inter-normal-12px text-indigo-700">
                            {files.length}
                          </span>
                        </>
                      </span>
                    )}
                  </span>
                ),
                content: (
                  <FilesDisplay env={env} files={files} isPreview={isPreview} />
                ),
                unmount: false,
              },
              ...(externalData
                ? externalData.map((report) => ({
                    key: report.node_name + report.provider,
                    label: (
                      <Tooltip
                        placement="top"
                        title={
                          report.connection_name ??
                          humanifyExternalReportName(
                            report.resource,
                            report.provider,
                          )
                        }
                      >
                        <div className="flex h-full items-center pt-1">
                          <ProviderIcon provider={report.provider} size="sm" />
                          <p className="ml-1.5">
                            {report.connection_name ??
                              humanifyExternalReportName(report.resource)}
                          </p>
                        </div>
                      </Tooltip>
                    ),
                    content: <ReportDisplay value={report.response} />,
                  }))
                : []),
            ].filter(Boolean as unknown as ExcludesFalse)}
          />
        ) : (
          <div className="mt-[46px] border-gray-200 border-t px-9 py-8">
            <SkeletonLine className="h-3 w-[117px]" />
            <SkeletonLine className="mt-4 h-3 w-[250px]" />
            {times(6, (index) => (
              <div key={index} className="mt-6 flex justify-between">
                <SkeletonLine className="h-3 w-[117px]" />
                <SkeletonLine className="h-3 w-[137px]" />
              </div>
            ))}
            <div className="mt-9 mb-[26px] border-gray-100 border-t" />
            <SkeletonLine className="h-3 w-[117px]" />
            <SkeletonLine className="mt-4 h-3 w-[250px]" />
            {times(3, (index) => (
              <div key={index} className="mt-6 flex justify-between">
                <SkeletonLine className="h-3 w-[117px]" />
                <SkeletonLine className="h-3 w-[137px]" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const InspectDataDisplay: React.FC<{
  data: GenericObject;
  env?: GetFileEnvFilesFileIdGetEnvEnum;
}> = ({ data, env }) => {
  return (
    <div className="space-y-4 px-9 py-8">
      {Object.entries(data).map(([key, value]) => (
        <DataRow key={key} env={env} fieldName={key} value={value} />
      ))}
    </div>
  );
};

const FileDisplayRowInner: React.FC<{
  icon: React.ReactNode;
  title: React.ReactNode;
  details: React.ReactNode;
  isErrored: boolean;
  isLoading: boolean;
  file: FileAttributes | File | undefined;
  isPreview?: boolean;
}> = ({
  icon,
  title,
  details,
  isErrored,
  isLoading,

  file,
  isPreview,
}) => {
  if (isLoading) {
    return (
      <div className="mx-4 mt-2 rounded-lg border border-gray-200">
        <SkeletonPlaceholder height="h-14" />
      </div>
    );
  }
  return (
    <div
      className={`mx-4 mt-2 rounded-lg ${isErrored && !isPreview ? "bg-gray-50" : "border border-gray-200"}`}
    >
      <div className="flex justify-between">
        <div className="px-2 py-2">{icon}</div>
        <div className="flex flex-1 flex-col py-2 pr-2">
          <span className="font-inter-medium-13px text-gray-800">{title}</span>
          <span className="font-inter-normal-12px text-gray-500">
            {details}
          </span>
        </div>

        {!isLoading && !isErrored && file && (
          <div className="flex flex-shrink-0 pt-2">
            <div className="pr-1">
              <FileViewer file={file}>
                <IconButton icon={faEye} size="xs" />
              </FileViewer>
            </div>
            <div className="pr-3">
              <FileDownloadButton file={file} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const FileDisplayRow: React.FC<{
  file: ReviewCaseHighlightsFile | File;
  env?: GetFileEnvFilesFileIdGetEnvEnum;
  isPreview?: boolean;
}> = ({ file, env, isPreview }) => {
  const { workspace } = useWorkspaceContext();

  const id = file instanceof File ? undefined : file.value;
  const isFileIdValid = isFileId(id);
  const fileId = isFileIdValid ? id : undefined;

  const {
    data: fileData,
    isLoading,
    error,
  } = useFile(fileId, env, workspace.base_url!);

  const isUploadedFile = file instanceof File;

  const fileAttributes = fileData?.data?.data?.attributes;
  const isFileIdInvalid = !isUploadedFile && !isFileIdValid;
  const isErrored = Boolean(error) || isFileIdInvalid;
  const isNotFound =
    error instanceof AxiosError && error.response?.status === 404;

  const getFileIcon = () => {
    if (isErrored && !isPreview) {
      return <Icon variant="error" icon={faExclamationTriangle} size="xs" />;
    }
    return (
      <Icon
        variant={isLoading ? "default" : "active"}
        icon={faFile}
        size="xs"
      />
    );
  };

  const getFileTitle = () => {
    if (isUploadedFile)
      return (
        <>
          <span className="pr-2">{file.name}</span>
          <Pill size="sm" variant="indigo-light">
            <span className="font-inter-normal-12px text-indigo-500">New</span>
          </Pill>
        </>
      );
    if (isPreview) {
      return "File(s)";
    }
    if (isFileIdInvalid) return "Invalid file ID";
    if (isLoading) return "Loading...";
    if (isNotFound) return "File not found";
    if (error) return "Error loading file";

    return fileAttributes?.name || "";
  };

  const getFileIdPill = ({ fileId }: { fileId: string | undefined }) => {
    if (isUploadedFile || !fileId) return null;
    return (
      <Pill size="sm" variant="gray">
        {getFilePillTitle(file.value)}
        <button
          className="ml-1 text-gray-400 hover:text-gray-600"
          onClick={() => {
            copyTextToClipboard(file.value);
            toastActions.success({ title: "Copied to clipboard!" });
          }}
        >
          <Icon icon={faCopy} size="xs" />
        </button>
      </Pill>
    );
  };

  const getFileDetails = () => {
    if (isUploadedFile) {
      return `${(FILE_TYPES[file.type as keyof typeof FILE_TYPES] || file.type).toUpperCase()} · ${humanReadableSize(file.size)}`;
    }
    if (isPreview) {
      return <>File(s) from {getFileIdPill({ fileId: file.value })}</>;
    }
    if (isNotFound)
      return (
        <>File with ID {getFileIdPill({ fileId: file.value })} not found</>
      );
    if (isFileIdInvalid)
      return <>File ID {getFileIdPill({ fileId: file.value })} is invalid</>;
    if (isErrored)
      return (
        <>
          File with ID {getFileIdPill({ fileId: file.value })} could not be
          loaded
        </>
      );

    if (!fileAttributes || isErrored) {
      return null;
    }

    const contentType = fileAttributes.content_type
      ? FILE_TYPES[fileAttributes.content_type as keyof typeof FILE_TYPES] ||
        fileAttributes.content_type
      : "";

    const size =
      fileAttributes.size !== undefined
        ? ` · ${humanReadableSize(fileAttributes.size)}`
        : "";

    const updatedAt = fileAttributes.created_at
      ? ` · Uploaded ${shortDateFromNow(fileAttributes.created_at)} ago`
      : "";

    return `${contentType}${size}${updatedAt}`;
  };

  return (
    <FileDisplayRowInner
      details={getFileDetails() || ""}
      file={isUploadedFile ? file : fileAttributes}
      icon={getFileIcon()}
      isErrored={isErrored}
      isLoading={isLoading}
      isPreview={isPreview}
      title={getFileTitle()}
    />
  );
};
const FilesDisplay: React.FC<{
  files: (ReviewCaseHighlightsFile | File)[];
  env?: GetFileEnvFilesFileIdGetEnvEnum;
  isPreview?: boolean;
}> = ({ files, env, isPreview }) => {
  if (files.length === 0) {
    return (
      <EmptyState
        description="Files that should be presented to the reviewer will show up here"
        headline="No files to display"
        icon={faFile}
      />
    );
  }
  return (
    <div className="mb-2 flex flex-col">
      {files.map((file, index) => {
        return (
          <FileDisplayRow
            key={index}
            env={env}
            file={file}
            isPreview={isPreview}
          />
        );
      })}
    </div>
  );
};

const HighlightsDisplay: React.FC<{
  env?: GetFileEnvFilesFileIdGetEnvEnum;
  highlights: ReviewCaseHighlightsElement[];
  isPreview?: boolean;
}> = ({ env, highlights, isPreview }) => {
  if (isPreview && highlights.length === 0) {
    return (
      <EmptyState
        description="Elements that should be highlighted for the reviewer will show up here"
        headline="No highlights exist yet"
        icon={faInputPipe}
      />
    );
  }
  return (
    <div className="space-y-4 px-9 py-8">
      {highlights.map((highlight, i) => {
        switch (highlight.type) {
          case "file":
            return null;
          case "field":
            return (
              <DataRow
                key={i}
                env={env}
                fieldName={highlight.readable_name}
                isPreview={isPreview}
                value={highlight.value}
              />
            );
          case "divider":
            return (
              <div key={i} className="py-4">
                <div className="border-gray-100 border-t" />
              </div>
            );
          case "description":
            return <MarkdownRenderer key={i} content={highlight.value} />;
          default:
            return assertUnreachable(highlight);
        }
      })}
    </div>
  );
};

const MAX_CONTENT_LENGTH = 280;

const TRUNCATED_VALUE_MESSAGE =
  "Value too large to display. Contact your workspace admin for full access.";

export const isTruncatedValue = (
  value: unknown,
): value is { __truncated: true; __original_size_bytes?: number } =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  (value as { __truncated?: unknown }).__truncated === true;

export const TruncatedValueDisplay: React.FC<{ alignRight?: boolean }> = ({
  alignRight = true,
}) => (
  <p
    className={twJoin(
      "min-w-0 flex-1 break-words font-inter-normal-12px text-gray-500 italic",
      alignRight && "text-right",
    )}
  >
    {TRUNCATED_VALUE_MESSAGE}
  </p>
);

export const DataValue: React.FC<{
  env?: GetFileEnvFilesFileIdGetEnvEnum;
  fieldName: string;
  value: JSONValue;
}> = ({ env, fieldName, value }) => {
  if (isTruncatedValue(value)) {
    return <TruncatedValueDisplay />;
  }

  if (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((v) => isFileId(v))
  ) {
    return (
      <div className="flex flex-wrap gap-1">
        {value.map((fileId: string, i: number) => (
          <FilePill key={i} env={env} fileId={fileId} />
        ))}
      </div>
    );
  }

  const isObjectValue = typeof value === "object" && value !== null;

  if (isObjectValue) {
    return <DataRowPopup content={value} />;
  }

  if (isProbablyMarkdown(value)) {
    return (
      <MultilineDataValue
        fieldName={fieldName}
        value={value}
        preserveFormattingForLargeStrings
      />
    );
  }

  if (isFileId(value)) {
    return <FilePill env={env} fileId={value} />;
  }

  return (
    <Tooltip
      body={formatValue(value)}
      disabled={String(value).length <= MAX_CONTENT_LENGTH}
      placement="top"
      asChild
    >
      <p className="line-clamp-5 min-w-0 flex-1 break-words text-right font-inter-normal-12px text-gray-800">
        {linkifyIfNeeded(formatValue(value))}
      </p>
    </Tooltip>
  );
};

const DataRow: React.FC<{
  env?: GetFileEnvFilesFileIdGetEnvEnum;
  fieldName: string;
  value: JSONValue;
  isPreview?: boolean;
}> = ({ env, fieldName, value, isPreview }) => {
  const isMultiline = isProbablyMarkdown(value);

  const isLongText =
    typeof value === "string" && value.length >= MAX_CONTENT_LENGTH;
  const isObjectValue = typeof value === "object" && value !== null;

  const shouldUseStackedLayout =
    (isMultiline || isLongText || isObjectValue) && !isPreview;

  return (
    <div
      className={twJoin(
        shouldUseStackedLayout
          ? "space-y-2"
          : "flex items-baseline justify-between",
      )}
    >
      <Tooltip
        body={fieldName}
        disabled={fieldName.length <= 50}
        placement="top"
        asChild
      >
        <p
          className={twJoin(
            "truncate font-inter-medium-12px text-gray-800",
            !shouldUseStackedLayout && "min-w-0 flex-2",
          )}
        >
          {fieldName}
        </p>
      </Tooltip>
      <div
        className={twJoin(
          shouldUseStackedLayout ? "[&_p]:text-left" : "min-w-0 flex-3",
        )}
      >
        {isPreview ? (
          <span className="font-inter-normal-12px text-gray-500">
            Value will be displayed here
          </span>
        ) : (
          <DataValue env={env} fieldName={fieldName} value={value} />
        )}
      </div>
    </div>
  );
};

const formatValue = (value: unknown) => {
  if (typeof value === "number") {
    return formatNumber(value);
  }

  return speakPythonPrimitive(String(value));
};

const linkifyIfNeeded = (value: unknown): React.ReactNode | string => {
  const isString = typeof value === "string";

  if (isString && isEmail(value)) {
    return (
      <a className="text-indigo-500" href={`mailto:${value}`}>
        {value}
      </a>
    );
  }

  if (isString && isValidURL(value)) {
    return (
      <a
        className="text-indigo-500"
        href={value.startsWith("http") ? value : `https://${value}`}
        rel="noreferrer"
        target="_blank"
      >
        {value}
      </a>
    );
  }

  return String(value);
};

const DataRowPopup: React.FC<{ content: GenericObject }> = ({ content }) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [cellBoundingRect, setBoundingRect] = useState<DOMRect | undefined>(
    undefined,
  );
  const cellDivRef = useRef<HTMLDivElement>(null);

  const getObjectDetailPosition = () => {
    if (!cellBoundingRect) return;
    return {
      bottom: window.innerHeight - cellBoundingRect.top,
      right: window.innerWidth - cellBoundingRect.right,
    };
  };

  const onMouseEnter = () => {
    const cRect = cellDivRef.current?.getBoundingClientRect();
    setBoundingRect(cRect);
    setTooltipOpen(true);
  };

  const onMouseLeave = () => {
    setTooltipOpen(false);
  };

  return (
    <div
      ref={cellDivRef}
      className="min-w-0 flex-1 text-right font-inter-normal-12px text-indigo-600"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <p className="truncate">
        {stringifiedObjectSpeakPython(JSON.stringify(content))}
      </p>
      {createPortal(
        <AnimatePresence>
          {tooltipOpen && (
            <m.div
              animate={{ opacity: 1, transition: { duration: 0.2 } }}
              className="z-50 max-h-150 min-w-[220px] max-w-128 overflow-auto text-left"
              exit={{ opacity: 0, transition: { duration: 0.1 } }}
              initial={{ opacity: 0 }}
            >
              <ObjectDetailPopover
                objectToDisplay={content}
                position="auto"
                positionStyles={getObjectDetailPosition()}
              />
            </m.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  );
};

const ReportDisplay: React.FC<{ value: GenericObject }> = ({ value }) => {
  if (isTruncatedValue(value)) {
    return (
      <div className="px-5 py-4">
        <TruncatedValueDisplay alignRight={false} />
      </div>
    );
  }

  return (
    <div className="flex flex-col divide-y divide-gray-200">
      {value.insights && (
        <>
          {Object.entries(value.insights).map(([insightKey, insightValue]) => (
            <div className="flex items-center justify-between px-5 py-4 font-inter-medium-12px">
              <div>{insightKey}</div>
              <div className="font-inter-normal-12px">
                {speakPythonPrimitive(String(insightValue))}
              </div>
            </div>
          ))}
        </>
      )}
      <div className="review-case-report-display relative flex-1 px-5 py-4">
        <div className="flex min-h-full flex-col">
          {value.insights && (
            <div className="pb-4 font-inter-medium-12px">Raw Response</div>
          )}
          <div className="flex flex-1">
            <ObjectDetailPane
              defaultInspectDepth={10}
              innerDimensionClass="border-gray-200 overflow-visible min-h-full w-full"
              jsonObject={value}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
